import { Field, TaskContentDetailsLocked } from '@cibo/core'
import { fieldNameColumn } from '@cibo/landmanager'
import { Loading } from '@cibo/profile'
import {
  fieldTaskEligibilityColumn,
  useProgramEngagementConfirmTask,
  useProgramEngagementFields,
  useProgramModel,
  useUpdateProgramEngagement,
} from '@cibo/programs'
import { ContentfulImage, DataGridPro, Section, useReasonMessages } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import { Box, Stack, Typography } from '@mui/material'
import { partition } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldTaskWarningColumn } from '../../../components/ProgramFieldColumns/fieldTaskWarningColumn'
import { TaskMarkdown } from '../TaskMarkdown'
import { DetailsLockedFailed } from './DetailsLockedFailed'
import { DetailsLockedPracticeConfirmation } from './DetailsLockedPracticeConfirmation'
import { DetailsLockedProps } from './types'
import { useHandleLockDetails } from './useHandleLockDetails'

export const DetailsLocked = ({
  engagement,
  ownerName,
  task,
  userRole,
  navigateToNextTask,
  ...rest
}: DetailsLockedProps) => {
  const { t } = useTranslation('@cibo/programs/DetailsLocked')
  const program = useProgramModel({ programId: engagement.programId })
  const taskState = engagement.getTaskStatus(task.id)
  const taskContent = program.data?.taskContent<TaskContentDetailsLocked>(task.id)
  const fields = useProgramEngagementFields({ id: engagement.id })
  const reasonMessages = useReasonMessages()
  const nameColumn = fieldNameColumn({ t })
  const reasonsColumn = fieldTaskEligibilityColumn({
    t,
    eligibility: taskState?.status?.eligibility,
    ...reasonMessages,
  })
  const warningColumn = fieldTaskWarningColumn({
    t,
    warnings: taskState?.status?.warnings,
  })
  const lockDetails = useProgramEngagementConfirmTask()
  const updateEngagement = useUpdateProgramEngagement()
  const handleLockDetails = useHandleLockDetails(engagement, task, navigateToNextTask)

  const [eligibleFields, ineligibleFields] = partition(
    ({ resourceId }) =>
      !taskState?.status.eligibility?.reasonItems?.some(item => item.id === resourceId),
    fields.data?.items ?? []
  )

  const { eligibleColumns, ineligibleColumns } = useMemo(
    () => ({
      eligibleColumns: [nameColumn, warningColumn],
      ineligibleColumns: [nameColumn, reasonsColumn],
    }),
    []
  )

  if (program.isLoading) {
    return <Loading />
  }

  const canConfirm = taskState?.allowedActions?.includes('canModify')

  if (taskState?.status?.eligibility?.eligible === false) {
    return (
      <DetailsLockedFailed
        {...rest}
        engagement={engagement}
        ownerName={ownerName}
        task={task}
        userRole={userRole}
      />
    )
  }

  return taskContent?.content?.variant &&
    taskContent.content?.variant === 'practiceConfirmation' ? (
    <DetailsLockedPracticeConfirmation
      handleLockDetails={handleLockDetails}
      taskState={taskState}
      taskContent={taskContent}
      fields={fields}
      userRole={userRole}
      ownerName={ownerName}
    />
  ) : (
    <Section sx={{ flex: 1 }}>
      <Stack padding={3} spacing={3}>
        {taskState?.available ? (
          <>
            <Stack
              direction="row"
              spacing={3}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              {program.data?.promoImages?.[0] && (
                <ContentfulImage
                  asset={program.data.promoImages[0]}
                  style={{ borderRadius: '50%', height: 75, width: 75 }}
                />
              )}
              <Box flex={1}>
                <TaskMarkdown>
                  {taskContent?.content.detailsLocked?.valid.markdown[userRole]?.replace(
                    '{{name}}',
                    ownerName
                  )}
                </TaskMarkdown>
              </Box>
              <Box alignSelf={'flex-start'}>
                <LoadingButton
                  loading={updateEngagement?.isLoading || lockDetails.isLoading}
                  onClick={handleLockDetails}
                  variant="contained"
                  disabled={!canConfirm}
                >
                  {t('continue')}
                </LoadingButton>
              </Box>
            </Stack>
            {!!eligibleFields.length && (
              <Section>
                <Stack padding={3} spacing={3}>
                  <Typography>{t('eligibleField', { count: eligibleFields.length })}</Typography>
                  <DataGridPro
                    columns={eligibleColumns}
                    rows={eligibleFields}
                    headerHeight={0}
                    getRowHeight={() => 'auto'}
                    getRowId={(row: Field) => row.resourceId}
                    loading={updateEngagement.isLoading || lockDetails.isLoading}
                    autoHeight
                    disableSelectionOnClick
                    hideFooter
                    hideCellFocus
                  />
                </Stack>
              </Section>
            )}
            {!!ineligibleFields.length && (
              <Section>
                <Stack padding={3} spacing={3}>
                  <Typography>
                    {t('ineligibleField', { count: ineligibleFields.length })}
                  </Typography>
                  <DataGridPro
                    columns={ineligibleColumns}
                    rows={ineligibleFields}
                    getRowHeight={() => 'auto'}
                    getRowId={(row: Field) => row.resourceId}
                    loading={updateEngagement.isLoading || lockDetails.isLoading}
                    autoHeight
                    disableSelectionOnClick
                    hideFooter
                    hideCellFocus
                  />
                </Stack>
              </Section>
            )}
          </>
        ) : (
          <>WIP: Not Available</>
        )}
      </Stack>
    </Section>
  )
}
